import angular from "angular";
import { Culture, DataMapper, DataMapperInfo, Exportable, FileExporter, ExportLogic, InputEnumOption, Translations } from "@salaxy/core";
import { AlertService, UiHelpers } from "../../services";

/**
 * Controller that provides export functionality for any data.
 */
export class ExportController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["AlertService", "UiHelpers"];

  /**
   * Maximum length of the data export.
   * Because of the implementation of the enum input, the value is number as a string.
   * -1 has a special meaning as the items that have already been loaded to the preview.
   */
  public maxCount = "0";

  /** Values available in the  */
  public maxCountOptions: InputEnumOption[] = [
    {
      value: "0",
      text: Translations.get("SALAXY.NG1.ExportComponent.exportLengths.all"),
    },
    {
      value: "-1",
      text: Translations.get("SALAXY.NG1.ExportComponent.exportLengths.current"),
    },
    {
      value: "1000",
      text: Translations.get("SALAXY.NG1.ExportComponent.exportLengths.short"),
    },
    {
      value: "10000",
      text: Translations.get("SALAXY.NG1.ExportComponent.exportLengths.long"),
    },
  ];

  /** Locale for exports */
  public localeId: Culture = Culture.Fi;

  /** Exporters supported by the current API. */
  public exporters: FileExporter[];

  /** Currently selected exporter. */
  public currentExporter: FileExporter;

  /** Current mapper once loaded. */
  public mapper: DataMapper<any, any> = null;

  /** Flag for export in progress: Shows a small spinner in the export button (wizard). */
  public isExporting = false;

  /** Messages from exports shown in the UI. */
  public exportMessage: string = null;

  /** All mappers defined by the current Exportable API. */
  public allMappers: DataMapperInfo[] = null;

  /** Available list item mappers for export */
  public listItemMappers: DataMapperInfo[] = null;

  /** Available object mappers for export */
  public objectMappers: DataMapperInfo[] = null;

  private _api: Exportable<any>;

  constructor(private alertService: AlertService, private uiHelpers: UiHelpers) {

  }

  /** Gets or sets the API that implements the Exportable interface. */
  public get api():Exportable<any> {
    return this._api;
  }
  public set api(value: Exportable<any>) {
    this._api = value;
    this.exporters = this.api.exporters ?? [];
    this.currentExporter = this.exporters[0];
    this.allMappers = this.api.getExportMappers();
    this.listItemMappers = this.api.getExportMappers().filter(x => (x.sourceSchema || "").indexOf("ListItem") > 0);
    this.objectMappers = this.api.getExportMappers().filter(x => (x.sourceSchema || "").indexOf("ListItem") < 0);
  }

  /**
   * Shows an export dialog for full export.
   * @param api Optional API implementing Exportable that is set to the controller.
   * The API must be set to the controller as property or in this parameter or showExport() will fail
   */
  public showExport(api: Exportable<any>) {
    this.api = api || this.api;
    if (!this.api) {
      throw new Error("Exportable API not specified in showExport()");
    }
    this.currentExporter = this.exporters[0];
    const loader = this.uiHelpers.showLoading();
    this.api.getExportMapper((this.listItemMappers[0] || this.allMappers[0]).uri).then((mapper) => {
      this.mapper = mapper;
      const dialogData = {
        $parent: this,
      };
      loader.dismiss();
      this.uiHelpers.showDialog("salaxy-components/modals/export/Export.html", null, dialogData, null, "lg");
    });
  }

  /**
   * Executes an immediate quick export - typically directly from a view.
   * Shows the Loader while data is being loaded for the export.
   * @param exporterId Exporter ID. If null, the first exporter in API is used.
   * @param api Optional API implementing Exportable that is set to the controller.
   * The API must be set to the controller as property or in this parameter or quickExport() will fail
   * @param mapper Optional mapper to use in the export. This may be the full DataMapperInfo object or just its uri.
   * If null and mapper has already been set, keeps the current mapper,
   * If null and current mapper is null, sets the first available list mapper, then any mapper. No mappers fails.
   */
  public quickExport(exporterId: string, api: Exportable<any>, mapper: DataMapperInfo | string = null): Promise<void> {
    this.api = api || this.api;
    if (!this.api) {
      throw new Error("Exportable API not specified in quickExport()");
    }
    const loader = this.uiHelpers.showLoading();
    return this.export(mapper, exporterId).then(() => {
      loader.dismiss();
    });
  }

  /**
   * Exports data to desired format.
   * @param mapper The mapper to use in the export. This may be the full DataMapperInfo object or just its uri.
   * If null and mapper has already been set, keeps the current mapper,
   * If null and current mapper is null, sets the first available list mapper, then any mapper. No mappers fails.
   * @param exporterId Identifies the exporter defining the file type and how the result is presented (copy-paste, download, send to external...).
   * If no exporter is selected for the controller, the first exporter in API is used.
   */
  public export(mapper: DataMapperInfo | string = null, exporterId: string = null): Promise<void> {
    if (exporterId) {
      this.currentExporter = this.exporters.find((x) => x.id == exporterId);
    }
    this.currentExporter = this.currentExporter || this.api.exporters[0];
    return this.setExportMapper(mapper).then(() => {
      this.isExporting = true;
      this.exportMessage = Translations.get("SALAXY.NG1.ExportComponent.export.creating");
      const notify = (message, index, count) => {
        this.exportMessage = `${message} (${index + 1}${count ? "/" + count : ""})`;
      };
      return ExportLogic.getExportData(this.api, this.mapper, Number(this.maxCount), notify).then((rows) => {
        this.isExporting = false;
        this.exportMessage = null;
        if (!rows || !rows.length) {
          this.alertService.addError(Translations.get("SALAXY.NG1.ExportComponent.export.noRows"));
          return;
        }
        this.currentExporter.export(rows, this.localeId);
      });
    });
  }

  /**
   * Loads the mapper definition and sets the current mapper to it.
   * @param newMapper New mapper to load and set as current: DataMapperInfo or its uri.
   * If null and mapper has already been set, keeps the current mapper,
   * If null and current mapper is null, sets the first available list mapper, then any mapper. No mappers fails.
   */
  public setExportMapper(newMapper: DataMapperInfo | string): Promise<DataMapper<any, any>> {
    let uri = (newMapper as DataMapperInfo)?.uri || newMapper as string;
    if (!uri) {
      if (this.mapper) {
        return Promise.resolve(this.mapper);
      } else {
        uri = (this.listItemMappers[0] || this.allMappers[0]).uri;
      }
    }
    if (this.mapper?.uri == uri) {
      return Promise.resolve(this.mapper);
    }
    this.mapper = null;
    return this.api.getExportMapper(uri).then((mapper) => {
      this.mapper = mapper;
      return mapper;
    });
  }

  /** Toggles the debug view on/off. */
  public toggleDebug() {
    const isDebug = !(this as any).isDebug;
    (this as any).isDebug = isDebug;
    const dialog = document.getElementById("salaxyDialog");
    if (dialog) {
      if (isDebug) {
        dialog.style.width = "98vw";
      } else {
        dialog.style.width = null;
      }
    }
  }
}
